<template>
  <div>
    <with-error-message :show-error="isXmlFeedUrlError">
      <span class="flex">
        <base-label id="url" label="XML Feed URL" :label-classes="'font-medium'"/>
        <vs-tooltip class="flex ml-1 items-center"
                    position="bottom"
                    @mouseenter.native="clearHideTimeout"
                    @mouseleave.native="setHideTimeout"
                    ref="xmlFeedBestPracticesRef"
        >
          <template #content>
            <div v-html="xmlFeedBestPracticesHint"/>
          </template>

          <vs-icon icon="info" class="text-gray cursor-default"/>
        </vs-tooltip>
      </span>
      <base-input id="url"
                  name="url"
                  placeholder="e.g. https://www.website.com/your-feed.xml"
                  variant="editor"
                  type="string"
                  class="w-full border-gray-border py-px mt-1 h-10"
                  :class="{'border-focus-error': isXmlFeedUrlError}"
                  :value="url"
                  @input="updateUrl"
      />
    </with-error-message>

    <span class="text-black font-semibold mt-5 flex">
      DEFINE XML ELEMENTS
      <vs-tooltip class="flex ml-1 items-center" position="bottom">
        <template #content>
          <div v-html="xmlElementsHint"/>
        </template>

        <vs-icon icon="info" class="text-gray cursor-default"/>
      </vs-tooltip>
    </span>

    <div class="flex flex-col mt-1">
      <with-error-message :show-error="isItemTagError" class="w-1/2 pr-2">
        <base-label id="item" label="Item Tag" :label-classes="'font-medium'"/>
        <base-input id="item"
                    name="item"
                    placeholder="<item-tag>"
                    variant="editor"
                    type="string"
                    class="w-full border-gray-border py-px mt-1 h-10"
                    :class="{'border-focus-error': isItemTagError}"
                    :value="item"
                    @input="updateItem"
                    @blur="stripAngleBrackets('item')"
        />
      </with-error-message>

      <div class="flex w-full gap-4 mt-3">
        <with-error-message :show-error="isItemNameTagError" class="w-1/2">
          <base-label id="itemName" label="Item Name Tag" :label-classes="'font-medium'"/>
          <base-select v-if="availableTags.length > 0"
                      :value="itemName"
                      :options="availableTags"
                      class="w-full h-10 mt-1"
                      @input="updateItemName"
          />
          <base-input v-else
                      id="itemName"
                      name="itemName"
                      placeholder="<item-name-tag>"
                      variant="editor"
                      type="string"
                      class="w-full border-gray-border py-px mt-1 h-10"
                      :class="{'border-focus-error': isItemNameTagError}"
                      :value="itemName"
                      @input="updateItemName"
                      @blur="stripAngleBrackets('itemName')"
          />
        </with-error-message>

        <with-error-message :show-error="isItemIdTagError" class="w-1/2">
          <base-label id="itemId" label="Item ID Tag" :label-classes="'font-medium'"/>
          <base-select v-if="availableTags.length > 0"
                      :value="itemId"
                      :options="availableTags"
                      class="w-full h-10 mt-1"
                      @input="updateItemId"
          />
          <base-input v-else
                      id="itemId"
                      name="itemId"
                      placeholder="<item-id-tag>"
                      variant="editor"
                      type="string"
                      class="w-full border-gray-border py-px mt-1 h-10"
                      :class="{'border-focus-error': isItemIdTagError}"
                      :value="itemId"
                      @input="updateItemId"
                      @blur="stripAngleBrackets('itemId')"
          />
        </with-error-message>
      </div>
    </div>

    <div v-if="isLoadingPreview || previewError || itemTagData" class="flex flex-col mt-5">
      <span class="text-black font-semibold flex">
        SEARCH CONFIGURATION
      </span>
      <div class="text-gray-dark mt-1 flex items-center">
        <span>Preview of first {{ itemOpeningTagPreview }}</span>
        <vs-tooltip class="flex ml-1 items-center"
                    position="top"
                    ref="vectorizationSkipTagsTooltipRef"
        >
          <template #content>
            <div v-html="vectorizationSkipTagsHint"/>
          </template>

          <vs-icon icon="info" class="text-gray cursor-default"/>
        </vs-tooltip>
      </div>
      <div v-bar class="bg-soft border rounded mt-1 p-3 font-mono text-sm" :class="{'h-80': itemTagData}">
        <div v-if="isLoadingPreview" class="flex items-center justify-center">
          <div class="text-gray">Loading preview...</div>
        </div>
        <div v-else-if="previewError" class="text-red-600">
          {{ previewError }}
        </div>
        <div v-else-if="itemTagData" class="whitespace-pre-wrap">
          <json-key-value
            v-for="(value, key) in itemTagData"
            :key="key"
            :key-name="key"
            :value="value"
            :path="key"
            :vectorization-skip-tags="vectorizationSkipTags"
            @toggle-key-skip="toggleKeySkip"
          />
        </div>
        <div v-else class="text-gray whitespace-pre-wrap">{}</div>
      </div>
    </div>
  </div>
</template>

<script>
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import Checkbox from '@/components/preview/Checkbox.vue'
import JsonKeyValue from './JsonKeyValue.vue'
import previewModule from '@/store/modules/preview.module'
import utilsService from '@/services/amio/utils.service'
import without from 'ramda/src/without'

export default {
  name: 'SettingsIndexesXmlFeed',
  components: { WithErrorMessage, Checkbox, JsonKeyValue },
  props: {
    url: String,
    item: String,
    itemName: String,
    itemId: String,
    vectorizationSkipTags: Array
  },
  data() {
    return {
      hideTimeout: null,
      itemTagData: null,
      isLoadingPreview: false,
      cachedUrl: null,
      previewError: null
    }
  },
  watch: {
    url: {
      immediate: true,
      handler(newUrl, oldUrl) {
        // Only fetch if URL actually changed
        // On initial mount (oldUrl is undefined), only fetch if URL is provided
        if (oldUrl === undefined) {
          if (newUrl) {
            this.fetchXmlPreview()
          }
        } else if (newUrl !== oldUrl) {
          this.fetchXmlPreview()
        }
      }
    }
  },
  methods: {
    updateUrl(updatedUrl) {
      if (updatedUrl === this.url) return

      if (this.isXmlFeedUrlError) this.error = null
      this.$emit('update:url', updatedUrl)
    },

    async fetchXmlPreview() {
      if (!this.url || !this.url.trim() || this.url === this.cachedUrl) {
        return
      }

      this.isLoadingPreview = true
      this.previewError = null
      this.cachedUrl = this.url

      try {
        const result = await utilsService.previewXmlFeed(this.url, this.item)
        if (result) {
          this.updateItem(result.itemTag || '')
          this.itemTagData = result.itemTagData
          this.$nextTick(() => {
            if (!this.itemTagData[this.itemName]) this.updateItemName('')
            if (!this.itemTagData[this.itemId]) this.updateItemId('')
          })
        }
        this.previewError = null
      } catch (error) {
        this.previewError = error.message || 'Failed to load XML feed preview'
      } finally {
        this.isLoadingPreview = false
      }
    },
    stripAngleBrackets(field) {
      const value = this[field]
      if (!value) return

      const stripped = value.replace(/[<>]/g, '')
      if (stripped !== value) {
        const updateMap = {
          item: 'update:item',
          itemName: 'update:item-name',
          itemId: 'update:item-id'
        }
        this.$emit(updateMap[field], stripped)
      }
    },
    updateItem(updatedItem) {
      this.$emit('update:item', updatedItem)
    },
    updateItemName(updatedItemName) {
      this.$emit('update:item-name', updatedItemName)
    },
    updateItemId(updatedItemId) {
      this.$emit('update:item-id', updatedItemId)
    },
    updateVectorizationSkipTags(updatedVectorizationSkipTags) {
      this.$emit('update:vectorization-skip-tags', updatedVectorizationSkipTags)
    },
    getAllChildPaths(parentPath) {
      const childPaths = []
      
      const traverse = (obj, currentPath) => {
        if (obj === null || obj === undefined) return
        
        if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            const itemPath = `${currentPath}[${index}]`
            // Check if this path is a child of the parent path
            if (itemPath !== parentPath && (itemPath.startsWith(parentPath + '.') || itemPath.startsWith(parentPath + '['))) {
              childPaths.push(itemPath)
            }
            if (typeof item === 'object' && item !== null) {
              traverse(item, itemPath)
            }
          })
        } else if (typeof obj === 'object') {
          Object.keys(obj).forEach(key => {
            const childPath = currentPath ? `${currentPath}.${key}` : key
            // Check if this path is a child of the parent path
            if (childPath !== parentPath && (childPath.startsWith(parentPath + '.') || childPath.startsWith(parentPath + '['))) {
              childPaths.push(childPath)
            }
            if (typeof obj[key] === 'object' && obj[key] !== null) {
              traverse(obj[key], childPath)
            }
          })
        }
      }
      
      if (this.itemTagData) {
        // Start traversing from root
        traverse(this.itemTagData, '')
      }
      
      return childPaths
    },
    toggleKeySkip(path, checked) {
      const currentSkipTags = this.vectorizationSkipTags || []
      const childPaths = this.getAllChildPaths(path)
      
      let updatedSkipTags
      if (checked) {
        // Checking: remove the path and all its children from skip tags
        updatedSkipTags = without([path, ...childPaths], currentSkipTags)
      } else {
        // Unchecking: add the path and all its children to skip tags
        const pathsToAdd = [path, ...childPaths]
        updatedSkipTags = [...currentSkipTags]
        pathsToAdd.forEach(p => {
          if (!updatedSkipTags.includes(p)) {
            updatedSkipTags.push(p)
          }
        })
      }
      
      this.updateVectorizationSkipTags(updatedSkipTags)
    },
    setHideTimeout() {
      // HACK: persist the tooltip for user to click on link
      this.$refs.xmlFeedBestPracticesRef?.mouseenterx()
      this.hideTimeout = setTimeout(() => {
        this.$refs.xmlFeedBestPracticesRef?.mouseleavex()
      }, 1500)
    },
    clearHideTimeout() {
      clearTimeout(this.hideTimeout)
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    availableTags() {
      return this.itemTagData ? Object.keys(this.itemTagData).sort() : []
    },

    xmlElementsHint() {
      return `In order to correctly process the XML feed, we need to know which tags contain some specific product information.
        <br/><br/>
        Item Tag - name of tag that wraps all information of a single product.
        <br/><br/>
        Item Name Tag - name of tag that contains the product name.
        <br/><br/>
        Item ID Tag - name of tag that contains unique indentifier of the product.
      `
    },
    xmlFeedBestPracticesHint() {
      return `For best results, ensure that your feed follows <a href="https://docs.amio.io/docs/bot-product-feeds-best-practices" target="_blank">best practices</a>.
        <br/>The feed must be pre-generated (i.e. just-in-time generation will not be accepted).'
      `
    },
    vectorizationSkipTagsHint() {
      return `• Keep only properties which will help chatbot find relevant products.
        <br/>
        • Irrelevant properties (like URLs or unnecessary IDs) harm search results
        <br/><br/>
        Note: This configuration is <strong>applied globally</strong> to all product feeds within your organization.
      `
    },
    itemOpeningTagPreview() {
      return `<${this.item || 'item-tag'}>`
    },
    isXmlFeedUrlError() {
      return this.errorDescription?.includes('URL') || this.errorDescription?.includes('File not found')
    },
    isItemTagError() {
      return this.errorDescription?.includes('Item Tag')
    },
    isItemNameTagError() {
      return this.errorDescription?.includes('Item Name Tag')
    },
    isItemIdTagError() {
      return this.errorDescription?.includes('Item ID Tag')
    }
  },
  destroyed() {
    if (this.isXmlFeedUrlError) this.error = null
  }
}
</script>
